import {chain, compact, unionBy} from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {intersperseNodes} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {TagsMap} from '../../../analytics/components/filters/common/tagSelectionDropdown';
import {filterRenderers} from '../../../analytics/helpers/analyticsFiltersHelpers';
import {globalNamespace} from '../../../helpers/models/namespaceHelpers';
import {buildTagSearchUrl, findParentTags, SearchTagStatusesEnum} from '../../../helpers/models/tagHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {ApiSearchTags, SearchTagModel} from '../../../models/api/searchTagModel';
import {ApiTag, TagBehaviorsEnum, TagModel} from '../../../models/api/tagModel';
import {fetchApiAsync} from '../../../services/http/http';
import {getCompany} from '../../../store/data/company/companySelectors';
import {updateDataAction} from '../../../store/data/dataGroupsActions';
import {getTagsTable} from '../../../store/data/tags/tagsSelectors';
import {DropdownItemTagIcon} from '../../common/dropdowns/dropdownItemTagIcon';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {
  maybeRenderDeduplicationIcon,
  renderFullTagName,
  renderTagNameInTooltip,
} from '../../common/dropdowns/tagDropdownItem';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {LensFormMultiselectProps} from './types';

// Starred Tags are personal tags, but this component is only intended for team tags at this time.
const isStarredTag = false;

/*
 * Props.
 */

interface LensFormMultiselectInboxesProps extends Omit<LensFormMultiselectProps<TagModel>, 'choices'> {
  namespace: string | undefined;
  inboxIds: ReadonlyArray<number>;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  ${ellipsis()}
`;

/*
 * Component.
 */

export const LensFormMultiSelectTags: FC<PropsWithChildren<LensFormMultiselectInboxesProps>> = (props) => {
  const {isDisabled, selectedIds, onSetItemIds, namespace, inboxIds} = props;

  const company = useAppSelector(getCompany);
  const [initialSelectedTags, setInitialSelectedTags] = useState<ReadonlyArray<SearchTagModel>>([]);
  // Tags that are available for using to render the dropdown button value.
  //  These are tags that have been selected at some point and may or may not be currently selected.
  //  We only store these to guarantee we can show the name of selected tags as the button value.
  const [tagsMap, setTagsMap] = useState<TagsMap>({});
  const {byId: tags, deletedById} = useAppSelector(getTagsTable);
  const dispatch = useAppDispatch();

  useInitialEffect(() => {
    const selectedTagsMap: TagsMap = selectedIds.reduce(
      (prev, tagId) => ({...prev, [tagId]: tags[tagId]}),
      {},
    );

    const fetchMissingTagsById = async () => {
      const fetchPromises = selectedIds
        .filter((id) => !deletedById[id])
        .map(async (id) => {
          if (selectedTagsMap[id]) {
            return null;
          }
          const url = `${company.url}/tags/${id}`;
          return fetchApiAsync(ApiTag, url);
        });

      const missingTags = compact(await Promise.all(fetchPromises));
      missingTags.forEach((tag) => {
        selectedTagsMap[tag.id] = tag;
      });

      // Add any missing tags into the state
      if (missingTags.length > 0) {
        dispatch(updateDataAction({tags: missingTags}));
      }
      setTagsMap(selectedTagsMap);
    };

    fetchMissingTagsById();
  });

  useEffect(() => {
    setInitialSelectedTags(
      selectedIds
        .filter((id) => !deletedById[id])
        .map((id) => {
          const tag = tagsMap[id] ?? tags[id];

          return [tag, ...findParentTags(tags, tag)];
        }),
    );
  }, [tags, tagsMap, selectedIds, deletedById]);

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(company, namespace, initialSelectedTags, inboxIds);
    return computeFilterDropdownSourceAsync(fetcher);
  }, [company, inboxIds, initialSelectedTags, namespace]);

  const buildRowGenerator: FilterRowGenerator<SearchTagModel> = useCallback(
    (searchTag, dropdownSelectedIds, makeOnItemSelect, isSearching, initialSelectedIds) => {
      // Tag search returns an array of tags. The array represents the tag and its parent tags,
      // with the child tag coming first in the array. The request returns every tag, regardless
      // of its place in the hierarchy.
      const currentTag = searchTag[0];
      const isChecked = dropdownSelectedIds.includes(currentTag.id);
      const isAtTop = initialSelectedIds.includes(currentTag.id);
      const isParentAtTop = searchTag[1] && initialSelectedIds.includes(searchTag[1].id);

      const onItemSelect = makeOnItemSelect(currentTag.id);
      const onSelect = () => {
        setTagsMap((prevTagsMap) => ({
          ...prevTagsMap,
          [currentTag.id]: currentTag,
        }));
        onItemSelect();
        // Add all tags w/parents to the state so we can display it later
        dispatch(updateDataAction({tags: searchTag}));
      };

      const filteredSearchTag = buildFilteredSearchTag(searchTag, isAtTop, initialSelectedIds);
      const shouldCollapse = isSearching || isAtTop || isParentAtTop;

      const nestingLevel =
        filteredSearchTag.length === 1 && shouldCollapse ? 0 : searchTag.length - filteredSearchTag.length;
      // If we are searching or the tag is at the top, we display the tag + it's path on the same line.
      // Otherwise, we display a tree nested view, children tags will also render any parents at the top on the same line
      const tagsPath = shouldCollapse ? filteredSearchTag : [];
      const renderTag = () => (
        <>
          <DropdownItemTagIcon tag={currentTag} nestingLevel={nestingLevel} />
          <DropdownItemContent isChecked={isChecked}>
            <StyledContentDiv>
              {renderFullTagName(currentTag, tagsPath, isStarredTag)}
              {maybeRenderDeduplicationIcon(currentTag)}
            </StyledContentDiv>
          </DropdownItemContent>
        </>
      );

      return {
        renderer: renderTag,
        onSelect,
        isSelected: isChecked,
        renderTooltip: () => renderTagNameInTooltip(currentTag, tagsPath),
        id: currentTag.id,
      };
    },
    [setTagsMap],
  );

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props, tagsMap)}
      renderPlaceholder={() => <Trans id="lens-form-multiselect-tags-placeholder">Select team tags</Trans>}
      label={
        props.label || <Trans id="lens-form-multiselect-tags-label">Filtered by any of these tags</Trans>
      }
      dropdownTitle={<Trans id="lens-form-multiselect-tags-dropdown-title">Team tags</Trans>}
      isDisabled={isDisabled}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">
          Field will be enabled once an inbox is selected.
        </Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(props: LensFormMultiselectInboxesProps, tagsMap: TagsMap) {
  const {selectedIds} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTags = chain(selectedIds)
    .map((id) => tagsMap[id])
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTags.map((t) => ({
          node: filterRenderers[AnalyticsFilterTypesEnum.FULL_TAGS].renderItem(t),
          key: String(t.id),
        })),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}

function buildFetcher(
  company: CompanyModel,
  namespace: string | undefined,
  initialSelectedItems: ReadonlyArray<SearchTagModel>,
  inboxIds: ReadonlyArray<number>,
): DropdownFetcher<SearchTagModel> {
  return async (query, tags, cancelToken) => {
    if (!namespace || inboxIds.length < 1) {
      return {
        items: [],
        canLoadMore: false,
      };
    }

    // Exclude initial items to compute the offset.
    const existingCount = tags.filter((tag) => tag).length;

    // Saved views can only be created in a specific team identified by its namespace.
    // Also include company tags.
    const namespaces = [globalNamespace, namespace];

    const url = buildTagSearchUrl(company, query, {
      namespaces,
      inboxIds,
      offset: existingCount,
      behaviors: [TagBehaviorsEnum.CAN_DISPLAY],
      status: SearchTagStatusesEnum.ACTIVE,
    });

    const searchResults = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    return {
      items: unionBy(initialSelectedItems, tags, searchResults.tags, '0.id'),
      canLoadMore: searchResults.tags.length === searchResults.limit,
    };
  };
}

function buildFilteredSearchTag(
  searchTag: SearchTagModel,
  isAtTop: boolean,
  initialSelectedIds: ReadonlyArray<number>,
) {
  // Return the full path if the tag is at the top
  if (isAtTop) {
    return searchTag;
  }

  let currentIndex = 0;
  // Goes through the array and filter out any parents/grandparents that aren't at the top
  return searchTag.filter((tag, index) => {
    if (index === 0 || (currentIndex === index && initialSelectedIds.includes(tag.id))) {
      currentIndex++;
      return true;
    }
    return false;
  });
}
